import React, { useEffect, useState } from 'react';

import { getSupplyCoverage, SupplyCell, SupplyCoverageView } from '../api/supply-health';
import { GRADE_LEVEL_LABELS, GradeLevel } from '../domain/catalog';
import { Kpi, KpiRow, Note, OpsPage, Pill, Row, Section, Table } from '../../../design-system/ops';

/**
 * Coverage — discoverable supply over the approved taxonomy (ADM-09-FR-09,
 * ADM-12-FR-01, the computable part of ADM-12-FR-08).
 *
 * Supply only, and the screen says so before it says anything else. BR-23's
 * Healthy / Thin / Recruit are percentages of registered demand, and demand is
 * blocked on AD-P06-03; a grid coloured green and amber from supply alone
 * would look like coverage and measure something else. The one class supply
 * can decide is the one BR-23 states outright — "No supply = zero supply" —
 * so empty cells say "Sin oferta" and every other cell shows its count and
 * nothing more.
 *
 * The thresholds are shown with their registry version anyway: they are the
 * numbers that will govern this grid, and AC-A09-04 asks that coverage be
 * reproducible from source data *and* threshold version.
 */

const gradeLabel = (grade: string): string => GRADE_LEVEL_LABELS[grade as GradeLevel] ?? grade;

const CellView: React.FC<{ cell: SupplyCell | undefined }> = ({ cell }) => {
  if (!cell || cell.supplyClass === 'NO_SUPPLY') {
    return (
      <span>
        <Pill tone="bad">Sin oferta</Pill>
      </span>
    );
  }
  return (
    <span title={cell.courseIds.join(', ')}>
      <b>
        {cell.courses} curso{cell.courses === 1 ? '' : 's'}
      </b>
      <br />
      <span className="mut">
        {cell.teachers} profesor{cell.teachers === 1 ? '' : 'es'}
      </span>
    </span>
  );
};

const AdminCoverage: React.FC = () => {
  const [view, setView] = useState<SupplyCoverageView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSupplyCoverage();
        if (!cancelled) setView(res);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar la cobertura');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <OpsPage title="Coverage">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!view) {
    return (
      <OpsPage title="Coverage">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const byKey = new Map(view.cells.map((c) => [`${c.subject}\u0000${c.grade}`, c]));
  const empty = view.cells.filter((c) => c.supplyClass === 'NO_SUPPLY').length;
  const { thresholds } = view;

  return (
    <OpsPage
      title="Coverage"
      subtitle={`Oferta descubrible por materia y grado · calculada ${new Date(
        view.computedAt,
      ).toLocaleString('es-MX')}`}
    >
      <Note tone="warn">
        Solo oferta. La demanda registrada no está disponible ({view.demand.blockedBy}), así que
        ninguna celda se clasifica como sana, escasa o de reclutamiento. Las edades tampoco (
        {view.age.blockedBy}): la cuadrícula usa los grados de la taxonomía aprobada.
      </Note>

      <KpiRow>
        <Kpi label="Cursos descubribles" value={view.discoverableCourses} />
        <Kpi
          label="Celdas sin oferta"
          value={`${empty} / ${view.cells.length}`}
          tone={empty > 0 ? 'bad' : 'neutral'}
          note="Sin oferta = cero oferta (BR-23)"
        />
        <Kpi
          label="Fuera de la taxonomía"
          value={view.outsideTaxonomy.length}
          tone={view.outsideTaxonomy.length > 0 ? 'warn' : 'neutral'}
          note="Cursos que no caen en ninguna celda"
        />
      </KpiRow>

      <Section
        title="Umbrales de salud de cobertura"
        subtitle="Vigentes en el registro. Se aplicarán cuando exista la demanda."
      >
        <Note>
          Sana desde {thresholds.healthyFromPct}% · Escasa desde {thresholds.thinFromPct}% ·
          Reclutar por debajo de {thresholds.thinFromPct}% · Sin oferta = cero oferta ·{' '}
          {thresholds.version || 'valores por defecto'}
        </Note>
      </Section>

      <Section title="Oferta por materia y grado">
        <Table
          columns={`140px repeat(${view.grades.length}, minmax(70px, 1fr))`}
          head={['Materia', ...view.grades.map(gradeLabel)]}
        >
          {view.subjects.map((subject) => (
            <Row key={subject}>
              <span>
                <b>{subject}</b>
              </span>
              {view.grades.map((grade) => (
                <CellView key={grade} cell={byKey.get(`${subject}\u0000${grade}`)} />
              ))}
            </Row>
          ))}
        </Table>
      </Section>

      {view.outsideTaxonomy.length > 0 ? (
        <Note tone="warn">
          Cursos publicados fuera de la taxonomía aprobada: {view.outsideTaxonomy.join(', ')}.
        </Note>
      ) : null}
    </OpsPage>
  );
};

export default AdminCoverage;
